import {
  Box,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  ThemeProvider,
  Typography,
  createTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { ChangeOffer, CustomerSentOffersState } from "../../models/models";
import { getRfq, setLoading } from "../../customerSentOffersSlice";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { integerRegex, twoDecimalsRegex } from "../../../../shared/validators";
import { FileModel, enumToArray } from "../../../../shared/models/models";
import { ProductConditionCode, productCondition } from "../../../../common/models/CommonModels";
import UploadFile from "../../../../shared/components/UploadFile";
import useTranslation from "../../../../shared/hooks/useTranslation";

const MainDetailsStep = () => {
  const { rfq } = useAppSelector<CustomerSentOffersState>((state: any) => state.customerSentOffers);
  const dispatch = useAppDispatch();

  const {
    register,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext<ChangeOffer>();

  const { id } = useParams();
  const [file, setFile] = useState<FileModel | null>(getValues("documentFile"));

  const handlerCreateFile = (file: FileModel | null) => {
    setValue("documentFile", file);
    setFile(file);
  };

  const init = async () => {
    dispatch(setLoading(true));
    if (id) {
      dispatch(getRfq(id));
    }
    register("documentFile", {});
    dispatch(setLoading(false));
  };

  useEffect(() => {
    init();
  }, []);

  const theme = createTheme({});
  const { translate } = useTranslation("common");

  return (
    <ThemeProvider theme={theme}>
      <Box className="mt-5">
        <Box className="heading-1">Create new offer</Box>

        <Typography fontSize={20} fontWeight={500} className="text-20-medium flex">
          for{"\u00A0"}
          <span>
            <Typography fontSize={20} fontWeight={700}>
              {rfq?.buyer.name}
            </Typography>
          </span>
        </Typography>
      </Box>

      <Box className="mt-5">
        <Box className="heading-2">Main product details</Box>
      </Box>

      <Box className="mt-4 grid grid-cols-2 gap-4">
        <Box>
          <InputLabel required>Price, €</InputLabel>
          <TextField
            className="w-full"
            type="number"
            {...register("unitPrice", {
              required: "Field is required",
              pattern: { value: twoDecimalsRegex, message: "Invalid value" },
              min: { value: 0.01, message: "Price must be at least 0.01" }
            })}
            error={!!errors.unitPrice}
            helperText={errors.unitPrice?.message}
          />
        </Box>

        <Box>
          <InputLabel required>Quantity</InputLabel>
          <TextField
            className="w-full"
            type="number"
            {...register("quantity", {
              required: "Field is required",
              pattern: { value: integerRegex, message: "Invalid value" },
              min: { value: 1, message: "Quantity must be at least 1" }
            })}
            error={!!errors.quantity}
            helperText={errors.quantity?.message}
          />
        </Box>
      </Box>

      <Box className="mt-4">
        <InputLabel>Condition</InputLabel>
        <RadioGroup row defaultValue={ProductConditionCode.new}>
          {enumToArray<ProductConditionCode>(ProductConditionCode).map((code) => (
            <FormControlLabel
              key={code}
              label={translate(`productCondition.${productCondition(code)}`)}
              value={code}
              control={<Radio sx={{ color: "#ACAEB3" }} {...register("productConditionCode")} />}
              sx={{ marginRight: 5 }}
            />
          ))}
        </RadioGroup>
      </Box>

      <Box className="mt-4">
        <InputLabel>Description</InputLabel>
        <TextField
          className="w-full textarea"
          multiline
          rows={3}
          {...register("description")}
          error={!!errors.description}
          helperText={errors.description?.message}
        />
      </Box>

      <Box className="mt-4">
        <InputLabel>Upload PDF</InputLabel>
        <Box className="grid grid-cols-2 mt-1 gap-4">
          <UploadFile file={file} variant="pdf" onChangeFile={handlerCreateFile} />
        </Box>
      </Box>

      <Box className="mt-2">
        <FormControlLabel
          control={<Checkbox {...register("isPriceBoundToDocument")} />}
          label={
            <Box className="text-16-medium" color="#333">
              Price is bound to terms and conditions in offer
            </Box>
          }
        />
      </Box>
    </ThemeProvider>
  );
};
export default MainDetailsStep;
